import { machines } from '../../content/machines'
import type { Locale } from '../../content/site'
import { site } from '../../content/site'
import { aliasTable } from './aliases'
import { isPresetName, presetNames } from '../theme'
import { discordActivityLabel, discordLabel } from '../discord'
import { cowsay, figlet, shortHash } from './art'
import { filters } from './filters'
import { blank, columns, duration, humanBytes, line, link, shortDate, span, text } from './format'
import { HOME, USER, buildTree, completePath, fileSize, lookup, resolvePath, type FsDir, type FsNode } from './fs'
import { t } from './messages'
import { contactLines, documentLines, linkLines, machineLines, machineSlugs, projectTable, projectLines, safeUrl, workLines } from './render'
import type { Command, Exec, Line, ShellState } from './types'

const ok = (lines: Line[] = [], extra: Omit<Partial<Exec>, 'lines' | 'status'> = {}): Exec => ({
  lines,
  status: 0,
  ...extra,
})

const err = (message: string, status = 1): Exec => ({ lines: [text(message, 'err')], status })

/* ------------------------------------------------------------------ files */

const sortNodes = (nodes: FsNode[]): FsNode[] =>
  [...nodes].sort((a, b) =>
    a.kind === b.kind ? a.name.localeCompare(b.name) : a.kind === 'dir' ? -1 : 1,
  )

const nodeStyle = (node: FsNode) => (node.kind === 'dir' ? 'dir' : node.name.startsWith('.') ? 'dim' : undefined)

const longFormat = (nodes: FsNode[], state: ShellState): Line[] => {
  const sizes = nodes.map((node) => (node.kind === 'file' ? fileSize(node, state) : 4096))
  const width = Math.max(4, ...sizes.map((size) => humanBytes(size).length))
  const total = Math.ceil(sizes.reduce((sum, size) => sum + size, 0) / 1024)
  return [
    text(`total ${total}`, 'dim'),
    ...nodes.map((node, index) =>
      line(
        span(`${node.mode} `, 'dim'),
        span(`1 ${USER} ${USER} `, 'dim'),
        span(humanBytes(sizes[index]).padStart(width), 'num'),
        span(` ${shortDate(node.mtime)} `, 'dim'),
        span(node.name, nodeStyle(node)),
      ),
    ),
  ]
}

const readFile = (path: string, state: ShellState, tree: FsDir): Line[] | string => {
  const node = lookup(tree, path)
  if (!node) return `No such file or directory`
  if (node.kind === 'dir') return `Is a directory`
  return node.render(state)
}

const treeLines = (node: FsDir, prefix = ''): { lines: Line[]; dirs: number; files: number } => {
  const children = node.children.filter((child) => !child.name.startsWith('.'))
  let dirs = 0
  let files = 0
  const lines: Line[] = []
  children.forEach((child, index) => {
    const last = index === children.length - 1
    lines.push(
      line(span(`${prefix}${last ? '└── ' : '├── '}`, 'dim'), span(child.name, nodeStyle(child))),
    )
    if (child.kind === 'dir') {
      dirs += 1
      const nested = treeLines(child, `${prefix}${last ? '    ' : '│   '}`)
      lines.push(...nested.lines)
      dirs += nested.dirs
      files += nested.files
    } else {
      files += 1
    }
  })
  return { lines, dirs, files }
}

/* ----------------------------------------------------------------- system */

const processTable = (state: ShellState): Line[] => [
  line(span('  PID TTY          TIME CMD', 'key')),
  ...[
    ['1337', 'zsh'],
    ['1402', 'fastfetch'],
    ['1571', 'discord-presence'],
    ['1618', 'ps'],
  ].map(([pid, cmd], index) =>
    line(
      span(pid.padStart(5), 'num'),
      span(' pts/0    ', 'dim'),
      span(duration(Math.floor(state.uptime() / (index + 3)))),
      span(` ${cmd}`, 'exec'),
    ),
  ),
]

/* ------------------------------------------------------------------ table */

const openTargets = (state: ShellState): Map<string, string> => {
  const targets = new Map<string, string>()
  site.links.forEach((item, index) => {
    const url = safeUrl(item.url)
    if (url) targets.set(`link-${index + 1}`, url)
  })
  for (const project of state.projects) {
    const url = project.links.map((item) => safeUrl(item.url)).find(Boolean)
    if (url) targets.set(`project-${project.slug}`, url)
  }
  return targets
}

const KEY_BINDINGS: readonly [string, { en: string; ru: string }][] = [
  ['Tab', { en: 'complete command, path or argument', ru: 'дополнить команду, путь или аргумент' }],
  ['Shift+Tab', { en: 'move focus out of the shell', ru: 'вывести фокус из оболочки' }],
  ['↑ / ↓', { en: 'walk the command history', ru: 'листать историю команд' }],
  ['Ctrl+R', { en: 'reverse-i-search the history', ru: 'обратный поиск по истории' }],
  ['Ctrl+A / Ctrl+E', { en: 'start / end of line', ru: 'начало / конец строки' }],
  ['Ctrl+U / Ctrl+K', { en: 'kill before / after the cursor', ru: 'удалить до / после курсора' }],
  ['Ctrl+W', { en: 'kill the previous word', ru: 'удалить предыдущее слово' }],
  ['Ctrl+C', { en: 'abort the line or the running output', ru: 'прервать строку или вывод' }],
  ['Ctrl+L', { en: 'clear the screen', ru: 'очистить экран' }],
  ['Ctrl+D', { en: 'exit on an empty line', ru: 'выход на пустой строке' }],
  ['Esc', { en: 'discard the current line', ru: 'сбросить текущую строку' }],
]

const keyBindingLines = (state: ShellState): Line[] => {
  const width = Math.max(...KEY_BINDINGS.map(([key]) => key.length)) + 3
  return KEY_BINDINGS.map(([key, description]) =>
    line(span(key.padEnd(width), 'accent'), span(description[state.locale], 'dim')),
  )
}

/** Filters double as commands: `grep pattern file` reads before it filters. */
const filterCommand = (name: keyof typeof filters, category: 'text'): Command => ({
  name,
  category,
  summary: filters[name].summary,
  usage: `${filters[name].usage} [file]`,
  run(args, state) {
    const operands = args.filter((value) => !value.startsWith('-'))
    // `grep` takes a pattern first, so it needs two operands to name a file.
    const target = name === 'grep' ? (operands.length > 1 ? operands.at(-1) : undefined) : operands.at(-1)
    if (!target) {
      return err(`usage: ${filters[name].usage} <file>  —  or pipe into it with |`, 2)
    }
    const source = readFile(resolvePath(target, state.cwd, HOME), state, buildTree(state))
    if (typeof source === 'string') return err(`${name}: ${target}: ${source}`)
    const result = filters[name].apply(source, args.slice(0, args.lastIndexOf(target)))
    return { lines: result.lines, status: result.status }
  },
  complete: (prefix, state) => completePath(prefix, state),
})

export const commands: Command[] = [
  /* ---------------------------------------------------------------- shell */
  {
    name: 'help',
    category: 'shell',
    summary: { en: 'list every command', ru: 'список всех команд' },
    usage: 'help [category]',
    run: (args, state) => ok(helpLines(state, args[0])),
  },
  {
    name: 'man',
    category: 'shell',
    summary: { en: 'show the manual for a command', ru: 'руководство по команде' },
    usage: 'man <command>',
    run(args, state) {
      if (!args[0]) return err('What manual page do you want?', 2)
      const command = find(args[0])
      if (!command) return err(`No manual entry for ${args[0]}`)
      return ok(manLines(command, state))
    },
    complete: (prefix) => visibleNames().filter((name) => name.startsWith(prefix)),
  },
  {
    name: 'clear',
    category: 'shell',
    summary: { en: 'clear the scrollback', ru: 'очистить экран' },
    usage: 'clear',
    run: () => ({ lines: [], status: 0, clear: true }),
  },
  {
    name: 'history',
    category: 'shell',
    summary: { en: 'show the command history', ru: 'история команд' },
    usage: 'history [-c]',
    run: (args, state) =>
      args.includes('-c')
        ? { lines: [], status: 0, historyClear: true }
        : ok(
            state.history.map((entry, index) =>
              line(span(String(index + 1).padStart(5), 'dim'), span('  '), span(entry)),
            ),
          ),
  },
  {
    name: 'echo',
    category: 'shell',
    summary: { en: 'print the arguments', ru: 'вывести аргументы' },
    usage: 'echo [text]',
    run: (args) => ok([text(args.join(' '))]),
  },
  {
    name: 'which',
    category: 'shell',
    summary: { en: 'locate a command', ru: 'найти команду' },
    usage: 'which <command>',
    run(args) {
      if (!args[0]) return err('which: missing operand', 2)
      const command = find(args[0])
      return command
        ? ok([line(span(`/usr/bin/${command.name}`, 'exec'))])
        : err(`which: no ${args[0]} in (/usr/local/bin:/usr/bin:/bin)`)
    },
    complete: (prefix) => visibleNames().filter((name) => name.startsWith(prefix)),
  },
  {
    name: 'type',
    category: 'shell',
    summary: { en: 'describe how a name resolves', ru: 'как разрешается имя' },
    usage: 'type <name>',
    run(args) {
      const name = args[0]
      if (!name) return err('type: missing operand', 2)
      // zsh distinguishes aliases, builtins and files; `which` does not.
      const alias = aliasTable()[name]
      if (alias) {
        return ok([line(span(name, 'exec'), span(' is an alias for '), span(alias, 'str'))])
      }
      const command = find(name)
      if (!command) return err(`type: ${name} not found`)
      if (command.name !== name) {
        return ok([line(span(name, 'exec'), span(' is an alias for '), span(command.name, 'str'))])
      }
      return ok([
        line(
          span(name, 'exec'),
          span(command.category === 'shell' ? ' is a shell builtin' : ` is /usr/bin/${name}`),
        ),
      ])
    },
    complete: (prefix) => visibleNames().filter((name) => name.startsWith(prefix)),
  },
  {
    name: 'keys',
    category: 'shell',
    aliases: ['bindkey'],
    summary: { en: 'list the key bindings', ru: 'список горячих клавиш' },
    usage: 'keys',
    run: (_args, state) => ok(keyBindingLines(state)),
  },
  {
    name: 'true',
    category: 'shell',
    hidden: true,
    summary: { en: 'succeed silently', ru: 'успешно завершиться' },
    usage: 'true',
    run: () => ok(),
  },
  {
    name: 'false',
    category: 'shell',
    hidden: true,
    summary: { en: 'fail silently', ru: 'завершиться с ошибкой' },
    usage: 'false',
    run: () => ({ lines: [], status: 1 }),
  },
  {
    name: 'seq',
    category: 'text',
    summary: { en: 'print a range of numbers', ru: 'вывести диапазон чисел' },
    usage: 'seq [first] <last>',
    run(args) {
      const values = args.map(Number).filter((value) => Number.isFinite(value))
      if (!values.length) return err('seq: missing operand', 2)
      const [first, last] = values.length === 1 ? [1, values[0]] : values
      if (last < first || last - first > 5_000) return err('seq: refusing an unbounded range', 2)
      return ok(
        Array.from({ length: last - first + 1 }, (_, index) => text(String(first + index), 'num')),
      )
    },
  },
  {
    name: 'yes',
    category: 'text',
    summary: { en: 'repeat a string (bounded here)', ru: 'повторять строку (с ограничением)' },
    usage: 'yes [text]',
    run: (args) => ok(Array.from({ length: 100 }, () => text(args.join(' ') || 'y'))),
  },
  {
    name: 'alias',
    category: 'shell',
    summary: { en: 'list shell aliases', ru: 'список алиасов' },
    usage: 'alias',
    run: () =>
      ok(
        [
          ...Object.entries(aliasTable()),
          ...commands.flatMap((command) =>
            (command.aliases ?? []).map((alias) => [alias, command.name] as [string, string]),
          ),
        ]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([alias, target]) =>
            line(span(alias, 'exec'), span('='), span(`'${target}'`, 'str')),
          ),
      ),
  },
  {
    name: 'env',
    category: 'shell',
    aliases: ['printenv'],
    summary: { en: 'print the environment', ru: 'переменные окружения' },
    usage: 'env',
    run: (_args, state) =>
      ok([
        ...Object.entries(state.env).map(([name, value]) =>
          line(span(name, 'key'), span('='), span(value, 'str')),
        ),
        line(span('PWD', 'key'), span('='), span(state.cwd, 'str')),
        line(span('?', 'key'), span('='), span(String(state.status), 'num')),
      ]),
  },
  {
    name: 'exit',
    category: 'shell',
    aliases: ['logout', 'quit'],
    summary: { en: 'leave the session', ru: 'завершить сессию' },
    usage: 'exit',
    run: (_args, state) => ok([text(t('exitHint', state.locale), 'dim')]),
  },

  /* ---------------------------------------------------------------- files */
  {
    name: 'pwd',
    category: 'files',
    summary: { en: 'print the working directory', ru: 'текущий каталог' },
    usage: 'pwd',
    run: (_args, state) => ok([text(state.cwd)]),
  },
  {
    name: 'ls',
    category: 'files',
    aliases: ['dir'],
    summary: { en: 'list directory contents', ru: 'содержимое каталога' },
    usage: 'ls [-l] [-a] [path]',
    man: {
      en: ['Directories are highlighted. `-l` prints mode, size and mtime,', '`-a` includes dotfiles. `ll` is an alias for `ls -la`.'],
      ru: ['Каталоги подсвечены. `-l` печатает права, размер и время,', '`-a` показывает скрытые файлы. `ll` — алиас для `ls -la`.'],
    },
    run(args, state) {
      const flags = args.filter((value) => value.startsWith('-')).join('')
      const target = args.find((value) => !value.startsWith('-')) ?? '.'
      const path = resolvePath(target, state.cwd, HOME)
      const node = lookup(buildTree(state), path)
      if (!node) return err(`ls: cannot access '${target}': No such file or directory`, 2)
      if (node.kind === 'file') return ok([line(span(node.name))])

      const all = flags.includes('a')
      // Real `ls -a` lists the directory itself and its parent.
      const dots: FsNode[] = all
        ? [
            { ...node, name: '.' },
            { ...(lookup(buildTree(state), resolvePath('..', path, HOME)) ?? node), name: '..' },
          ]
        : []
      const entries = sortNodes([
        ...dots,
        ...node.children.filter((child) => all || !child.name.startsWith('.')),
      ])
      if (flags.includes('l')) return ok(longFormat(entries, state))
      if (!entries.length) return ok()

      // Same grid as format.columns(), but each cell keeps its node's colour.
      const names = entries.map((child) => (child.kind === 'dir' ? `${child.name}/` : child.name))
      const cell = Math.max(...names.map((name) => name.length)) + 2
      const perRow = Math.max(1, Math.floor(80 / cell))
      const rows: Line[] = []
      for (let start = 0; start < names.length; start += perRow) {
        rows.push({
          spans: names
            .slice(start, start + perRow)
            .map((name, offset) => span(name.padEnd(cell), nodeStyle(entries[start + offset]))),
        })
      }
      return ok(rows)
    },
    complete: (prefix, state) => completePath(prefix, state),
  },
  {
    name: 'cd',
    category: 'files',
    summary: { en: 'change the working directory', ru: 'сменить каталог' },
    usage: 'cd [path]',
    run(args, state) {
      const path = resolvePath(args[0] ?? '~', state.cwd, HOME)
      const node = lookup(buildTree(state), path)
      if (!node) return err(`cd: no such file or directory: ${args[0]}`)
      if (node.kind !== 'dir') return err(`cd: not a directory: ${args[0]}`)
      return { lines: [], status: 0, cwd: path }
    },
    complete: (prefix, state) => completePath(prefix, state, true),
  },
  {
    name: 'cat',
    category: 'files',
    summary: { en: 'print a file', ru: 'вывести файл' },
    usage: 'cat <file>…',
    run(args, state) {
      if (!args.length) return err('cat: missing operand', 2)
      const tree = buildTree(state)
      const lines: Line[] = []
      let status = 0
      for (const target of args) {
        const result = readFile(resolvePath(target, state.cwd, HOME), state, tree)
        if (typeof result === 'string') {
          lines.push(text(`cat: ${target}: ${result}`, 'err'))
          status = 1
        } else {
          lines.push(...result)
        }
      }
      return { lines, status }
    },
    complete: (prefix, state) => completePath(prefix, state),
  },
  {
    name: 'tree',
    category: 'files',
    summary: { en: 'draw the directory tree', ru: 'дерево каталогов' },
    usage: 'tree [path]',
    run(args, state) {
      const path = resolvePath(args[0] ?? '.', state.cwd, HOME)
      const node = lookup(buildTree(state), path)
      if (!node || node.kind !== 'dir') return err(`tree: ${args[0] ?? '.'}: No such directory`)
      const result = treeLines(node)
      return ok([
        line(span(path, 'dir')),
        ...result.lines,
        blank(),
        line(
          span(String(result.dirs), 'num'),
          span(' directories, ', 'dim'),
          span(String(result.files), 'num'),
          span(' files', 'dim'),
        ),
      ])
    },
    complete: (prefix, state) => completePath(prefix, state, true),
  },
  {
    name: 'rm',
    category: 'files',
    hidden: true,
    summary: { en: 'remove files', ru: 'удалить файлы' },
    usage: 'rm [-rf] <path>',
    run: (_args, state) => err(`rm: ${t('rmDenied', state.locale)}`),
  },
  {
    name: 'vim',
    category: 'files',
    hidden: true,
    aliases: ['vi', 'nano', 'emacs', 'code'],
    summary: { en: 'edit a file', ru: 'редактировать файл' },
    usage: 'vim <file>',
    run: (_args, state) => ok([text(t('editorHint', state.locale), 'warn')]),
  },

  /* --------------------------------------------------------------- system */
  {
    name: 'whoami',
    category: 'system',
    summary: { en: 'print the current user', ru: 'текущий пользователь' },
    usage: 'whoami',
    run: (_args, state) => ok([text(state.env.USER)]),
  },
  {
    name: 'id',
    category: 'system',
    summary: { en: 'print user and group ids', ru: 'идентификаторы пользователя' },
    usage: 'id',
    run: () =>
      ok([text(`uid=1000(${USER}) gid=1000(${USER}) groups=1000(${USER}),998(wheel),959(docker)`)]),
  },
  {
    name: 'hostname',
    category: 'system',
    summary: { en: 'print the host name', ru: 'имя машины' },
    usage: 'hostname',
    run: (_args, state) => ok([text(state.env.HOST)]),
  },
  {
    name: 'uname',
    category: 'system',
    summary: { en: 'print system information', ru: 'информация о системе' },
    usage: 'uname [-a|-r|-s]',
    run(args) {
      const release = '6.12.10-arch1-1'
      if (args.includes('-r')) return ok([text(release)])
      if (args.includes('-s')) return ok([text('Linux')])
      return ok([text(`Linux cadillac ${release} #1 SMP PREEMPT_DYNAMIC x86_64 GNU/Linux`)])
    },
  },
  {
    name: 'uptime',
    category: 'system',
    summary: { en: 'how long the session has run', ru: 'время работы сессии' },
    usage: 'uptime',
    run: (_args, state) =>
      ok([
        line(
          span(` ${state.now().toTimeString().slice(0, 8)} `, 'dim'),
          span('up '),
          span(duration(state.uptime()), 'num'),
          span(',  1 user,  load average: ', 'dim'),
          span('0.42, 0.51, 0.48', 'num'),
        ),
      ]),
  },
  {
    name: 'date',
    category: 'system',
    summary: { en: 'print the current date', ru: 'текущая дата' },
    usage: 'date',
    run: (_args, state) => ok([text(state.now().toString())]),
  },
  {
    name: 'ps',
    category: 'system',
    summary: { en: 'list running processes', ru: 'список процессов' },
    usage: 'ps',
    run: (_args, state) => ok(processTable(state)),
  },
  {
    name: 'top',
    category: 'system',
    aliases: ['htop'],
    summary: { en: 'snapshot of system load', ru: 'снимок загрузки системы' },
    usage: 'top',
    run: (_args, state) =>
      ok(
        [
          line(
            span('top - ', 'key'),
            span(state.now().toTimeString().slice(0, 8)),
            span(' up ', 'dim'),
            span(duration(state.uptime()), 'num'),
            span(',  load average: 0.42, 0.51, 0.48', 'dim'),
          ),
          line(span('Tasks:', 'key'), span(' 284 total,   1 running, 283 sleeping')),
          line(span('%Cpu(s):', 'key'), span('  4.1 us,  1.2 sy,  0.0 ni, '), span('94.5 id', 'ok')),
          line(span('MiB Mem :', 'key'), span('  32690 total,  14210 free,   9312 used,   9168 buff/cache')),
          blank(),
          ...processTable(state),
        ],
        { stream: 24 },
      ),
  },
  {
    name: 'df',
    category: 'system',
    summary: { en: 'report filesystem usage', ru: 'использование диска' },
    usage: 'df [-h]',
    run: () =>
      ok([
        line(span('Filesystem      Size  Used Avail Use% Mounted on', 'key')),
        text('/dev/nvme0n1p2  916G  412G  458G  48% /'),
        text('/dev/nvme0n1p1  1.0G  312M  713M  31% /boot'),
        text('tmpfs            16G   82M   16G   1% /dev/shm'),
      ]),
  },
  {
    name: 'free',
    category: 'system',
    summary: { en: 'report memory usage', ru: 'использование памяти' },
    usage: 'free [-h]',
    run: () =>
      ok([
        line(span('               total        used        free      shared  buff/cache   available', 'key')),
        text('Mem:            31Gi       9.1Gi        13Gi       1.2Gi       8.4Gi        21Gi'),
        text('Swap:          8.0Gi          0B       8.0Gi'),
      ]),
  },
  {
    name: 'fastfetch',
    category: 'system',
    aliases: ['neofetch', 'ff'],
    summary: { en: 'show a machine summary', ru: 'сводка по машине' },
    usage: 'fastfetch [machine]',
    man: {
      en: ['With no argument the main workstation `cadillac` is shown.', `Known machines: ${machineSlugs.join(', ')}.`],
      ru: ['Без аргумента показывает основную машину `cadillac`.', `Доступные машины: ${machineSlugs.join(', ')}.`],
    },
    run(args) {
      const slug = args[0] ?? 'cadillac'
      const lines = machineLines(slug)
      return lines.length ? ok(lines) : err(`fastfetch: ${slug}: machine not found`)
    },
    complete: (prefix) => machineSlugs.filter((slug) => slug.startsWith(prefix)),
  },
  {
    name: 'ping',
    category: 'system',
    summary: { en: 'ping a host', ru: 'проверить хост' },
    usage: 'ping <host>',
    run(args) {
      const host = args.find((value) => !value.startsWith('-'))
      if (!host) return err('ping: usage error: Destination address required', 2)
      const known = /^((?:[a-z0-9-]+\.)?mk69\.dev|nekonode\.ltd|localhost|127\.0\.0\.1)$/i.test(host)
      if (!known) return err(`ping: ${host}: Name or service not known`, 2)
      const times = [12.4, 11.8, 13.9, 12.1]
      return ok(
        [
          text(`PING ${host} (185.209.30.69) 56(84) bytes of data.`),
          ...times.map((value, index) =>
            line(
              span('64 bytes from '),
              span(host, 'accent'),
              span(`: icmp_seq=${index + 1} ttl=57 time=`, 'dim'),
              span(`${value.toFixed(1)} ms`, 'num'),
            ),
          ),
          blank(),
          line(span(`--- ${host} ping statistics ---`, 'key')),
          line(span('4 packets transmitted, 4 received, '), span('0% packet loss', 'ok'), span(', time 3005ms')),
          text('rtt min/avg/max/mdev = 11.800/12.550/13.900/0.783 ms', 'dim'),
        ],
        { stream: 320 },
      )
    },
  },
  {
    name: 'curl',
    category: 'system',
    summary: { en: 'fetch a URL', ru: 'запросить URL' },
    usage: 'curl [-I] <url>',
    run(args) {
      const target = args.find((value) => !value.startsWith('-'))
      if (!target) return err('curl: try \'curl --help\' for more information', 2)
      const url = safeUrl(target.startsWith('http') ? target : `https://${target}`)
      if (!url) return err(`curl: (1) Protocol not supported`, 1)
      const host = new URL(url).hostname
      if (!/(^|\.)mk69\.dev$/.test(host)) {
        return err(`curl: (6) Could not resolve host: ${host}`, 6)
      }
      return ok(
        [
          line(span('HTTP/2 200', 'ok')),
          ...[
            ['server', 'nginx'],
            ['content-type', 'text/html; charset=utf-8'],
            ['cache-control', 'public, max-age=60, stale-while-revalidate=300'],
            ['strict-transport-security', 'max-age=63072000'],
            ['x-powered-by', 'spite and nicotine'],
          ].map(([key, value]) => line(span(`${key}: `, 'key'), span(value, 'str'))),
        ],
        { stream: 40 },
      )
    },
  },
  {
    name: 'git',
    category: 'system',
    summary: { en: 'inspect the pseudo repository', ru: 'осмотреть псевдо-репозиторий' },
    usage: 'git <status|log|remote>',
    run(args, state) {
      const sub = args[0] ?? 'status'
      if (sub === 'remote') {
        return ok([
          line(span('origin\t', 'key'), span('https://github.com/MKultra6969', 'str'), span(' (fetch)', 'dim')),
          line(span('origin\t', 'key'), span('https://github.com/MKultra6969', 'str'), span(' (push)', 'dim')),
        ])
      }
      if (sub === 'log') {
        return ok(
          state.projects.map((project) =>
            line(
              span(shortHash(project.slug), 'warn'),
              span(' '),
              span(`(${project.year ?? '----'})`, 'dim'),
              span(' '),
              span(project.title[state.locale]),
            ),
          ),
        )
      }
      if (sub === 'status') {
        return ok([
          line(span('On branch '), span('main', 'ok')),
          text("Your branch is up to date with 'origin/main'.", 'dim'),
          blank(),
          text('nothing to commit, working tree clean'),
        ])
      }
      return err(`git: '${sub}' is not a git command. See 'git --help'.`)
    },
    complete: (prefix) => ['status', 'log', 'remote'].filter((value) => value.startsWith(prefix)),
  },
  {
    name: 'sudo',
    category: 'system',
    hidden: true,
    aliases: ['su', 'doas'],
    summary: { en: 'execute as another user', ru: 'выполнить от другого пользователя' },
    usage: 'sudo <command>',
    run: (_args, state) =>
      ({
        lines: [
          text(t('sudoLecture', state.locale), 'dim'),
          blank(),
          text('    #1) Respect the privacy of others.', 'dim'),
          text('    #2) Think before you type.', 'dim'),
          text('    #3) With great power comes great responsibility.', 'dim'),
          blank(),
          text(t('sudoDenied', state.locale), 'err'),
        ],
        status: 1,
        stream: 90,
      }),
  },

  /* ----------------------------------------------------------------- site */
  {
    name: 'portfolio',
    category: 'site',
    aliases: ['gui', 'startx'],
    summary: { en: 'open the portfolio in a GUI window', ru: 'открыть портфолио в GUI-окне' },
    usage: 'portfolio',
    man: {
      en: [
        'Boots a fake X11 session and maps the portfolio app on top of the',
        'terminal. Minimise suspends it (bring it back with `fg`), the close',
        'button or `Esc` quits it.',
      ],
      ru: [
        'Поднимает фейковую X11-сессию и открывает портфолио поверх',
        'терминала. Сворачивание приостанавливает его (вернуть — `fg`),',
        'кнопка закрытия или `Esc` завершают приложение.',
      ],
    },
    run(_args, state) {
      if (state.gui === 'open') return err('portfolio: already running on display :0')
      if (state.gui === 'suspended') {
        return ok([line(span('[1]  + continued  ', 'dim'), span('portfolio', 'exec'))], {
          gui: 'resume',
        })
      }
      const count = state.loading ? '…' : String(state.projects.length)
      return ok(
        [
          line(span('[xinit]     ', 'dim'), span('connecting to display '), span(':0', 'num')),
          line(span('[xinit]     ', 'dim'), span('X.Org X Server 21.1.16, screen 0: 2560x1440')),
          line(span('[mkwm]      ', 'dim'), span('compositor ready, '), span('vsync on', 'ok')),
          line(span('[portfolio] ', 'accent'), span('GTK_THEME=', 'key'), span('mk69-dark', 'str')),
          line(span('[portfolio] ', 'accent'), span('index: '), span(count, 'num'), span(' projects')),
          line(span('[portfolio] ', 'accent'), span('mapping window '), span('1200x780+80+60', 'num'), span(' on '), span(':0', 'num')),
          text(t('guiHint', state.locale), 'dim'),
        ],
        { gui: 'open', stream: 90 },
      )
    },
  },
  {
    name: 'fg',
    category: 'shell',
    summary: { en: 'resume the suspended window', ru: 'вернуть свёрнутое окно' },
    usage: 'fg',
    run(_args, state) {
      if (state.gui !== 'suspended') return err('fg: no current job')
      return ok([line(span('[1]  + continued  ', 'dim'), span('portfolio', 'exec'))], {
        gui: 'resume',
      })
    },
  },
  {
    name: 'jobs',
    category: 'shell',
    summary: { en: 'list background jobs', ru: 'список фоновых задач' },
    usage: 'jobs',
    run: (_args, state) =>
      state.gui === 'suspended'
        ? ok([line(span('[1]  + suspended  ', 'dim'), span('portfolio', 'exec'))])
        : ok(),
  },
  {
    name: 'projects',
    category: 'site',
    summary: { en: 'browse the portfolio', ru: 'портфолио' },
    usage: 'projects [slug]',
    man: {
      en: ['Without an argument, prints the whole index.', 'With a slug, prints the full card including a preview image.'],
      ru: ['Без аргумента печатает весь список.', 'Со slug печатает полную карточку с превью.'],
    },
    run(args, state) {
      if (state.loading) return ok([text(t('projectsLoading', state.locale), 'dim')])
      if (state.failed) return err(t('projectsFailed', state.locale))
      if (!args[0]) {
        return ok([
          ...projectTable(state.projects, state.locale),
          blank(),
          text(t('guiTip', state.locale), 'dim'),
        ])
      }
      const project = state.projects.find((item) => item.slug === args[0])
      return project
        ? ok(projectLines(project, state.locale))
        : err(`projects: ${args[0]}: not found`)
    },
    complete: (prefix, state) =>
      state.projects.map((project) => project.slug).filter((slug) => slug.startsWith(prefix)),
  },
  {
    name: 'machines',
    category: 'site',
    summary: { en: 'list the machines', ru: 'список машин' },
    usage: 'machines [slug]',
    run(args, state) {
      if (!args[0]) {
        const width = Math.max(...machines.map((machine) => machine.slug.length)) + 2
        return ok(
          machines.map((machine) =>
            line(
              span(machine.slug.padEnd(width), 'dir'),
              span(machine.role[state.locale].padEnd(24)),
              span(machine.specs.join(' · '), 'dim'),
            ),
          ),
        )
      }
      const lines = machineLines(args[0])
      return lines.length ? ok(lines) : err(`machines: ${args[0]}: not found`)
    },
    complete: (prefix) => machineSlugs.filter((slug) => slug.startsWith(prefix)),
  },
  {
    name: 'links',
    category: 'site',
    summary: { en: 'every public link', ru: 'все публичные ссылки' },
    usage: 'links',
    // Same source as `cat ~/links.txt`, so editing one in the CRM moves both.
    run: (_args, state) =>
      ok(documentLines(state.documents, 'links.txt', state.locale) ?? linkLines()),
  },
  {
    name: 'contact',
    category: 'site',
    summary: { en: 'how to reach me', ru: 'как связаться' },
    usage: 'contact',
    run: (_args, state) =>
      ok(documentLines(state.documents, 'contact.txt', state.locale) ?? contactLines(state.locale)),
  },
  {
    name: 'work',
    category: 'site',
    aliases: ['job'],
    summary: { en: 'where I work right now', ru: 'где я сейчас работаю' },
    usage: 'work',
    man: {
      en: ['Prints `~/work.txt`: the current employer and role.', 'What exactly I do there is under NDA, so do not expect more.'],
      ru: ['Печатает `~/work.txt`: текущее место работы и роль.', 'Чем именно занимаюсь — под NDA, так что больше не будет.'],
    },
    run: (_args, state) =>
      ok(documentLines(state.documents, 'work.txt', state.locale) ?? workLines(state.locale)),
  },
  {
    name: 'open',
    category: 'site',
    summary: { en: 'open a link in a new tab', ru: 'открыть ссылку в новой вкладке' },
    usage: 'open <target>',
    run(args, state) {
      const targets = openTargets(state)
      const url = args[0] ? targets.get(args[0]) : undefined
      if (!url) {
        return {
          lines: [
            text(`open: ${args[0] ?? ''}: unknown target`, 'err'),
            text('known targets:', 'dim'),
            ...columns([...targets.keys()], 80, 'accent'),
          ],
          status: 2,
        }
      }
      return ok([line(span('opening '), link(url, url))], { open: url })
    },
    complete: (prefix, state) =>
      [...openTargets(state).keys()].filter((value) => value.startsWith(prefix)),
  },
  {
    name: 'lang',
    category: 'site',
    summary: { en: 'switch between English and Russian', ru: 'переключить язык' },
    usage: 'lang [en|ru]',
    run(args, state) {
      const requested = args[0]?.toLowerCase()
      if (requested !== undefined && requested !== 'en' && requested !== 'ru') {
        return err(`lang: ${args[0]}: unknown locale (en, ru)`, 2)
      }
      const locale: Locale = requested ?? (state.locale === 'en' ? 'ru' : 'en')
      return ok([text(t('langSwitched', locale), 'ok')], { locale })
    },
    complete: (prefix) => ['en', 'ru'].filter((value) => value.startsWith(prefix)),
  },
  {
    name: 'theme',
    category: 'site',
    summary: { en: 'change the colour palette', ru: 'сменить палитру' },
    usage: 'theme <list|set <name>|reset>',
    run(args, state) {
      const sub = args[0]
      if (!sub || sub === 'list') {
        return ok([
          text(t('themeUsage', state.locale), 'dim'),
          blank(),
          ...columns(presetNames, 80, 'accent'),
        ])
      }
      if (sub === 'reset') return ok([text(t('themeReset', state.locale), 'ok')], { theme: 'reset' })
      if (sub === 'set') {
        const name = args[1]
        if (!name || !isPresetName(name)) {
          return err(`theme: ${name ?? ''}: unknown palette (${presetNames.join(', ')})`, 2)
        }
        return ok([line(span(`${t('themeApplied', state.locale)}: `), span(name, 'accent'))], { theme: name })
      }
      return err(t('themeUsage', state.locale), 2)
    },
    complete: (prefix) =>
      ['list', 'set', 'reset', ...presetNames].filter((value) => value.startsWith(prefix)),
  },
  {
    name: 'discord',
    category: 'site',
    summary: { en: 'show my Discord presence', ru: 'статус в Discord' },
    usage: 'discord',
    run(_args, state) {
      const presence = state.discord
      const badge =
        presence.state === 'online' ? 'ok' : presence.state === 'dnd' ? 'err' : presence.state === 'idle' ? 'warn' : 'dim'
      return ok([
        line(span('discord: ', 'dim'), span(discordLabel({ ...presence, activities: undefined }), badge)),
        ...(presence.activities ?? []).map((activity) =>
          line(span('  • ', 'accent'), span(discordActivityLabel(activity))),
        ),
      ])
    },
  },
  {
    name: 'omz',
    category: 'site',
    summary: { en: 'oh-my-zsh information', ru: 'информация об oh-my-zsh' },
    usage: 'omz <version|plugin list>',
    run(args) {
      const sub = args.join(' ')
      if (!sub || sub === 'help') return ok([text('Usage: omz <version|plugin list>', 'dim')])
      if (sub === 'version') return ok([text('oh-my-zsh master · theme robbyrussell')])
      if (sub === 'plugin list') return ok([text('git'), text('zsh-autosuggestions')])
      return err(`omz: unknown command: ${sub}`)
    },
    complete: (prefix) => ['version', 'plugin list'].filter((value) => value.startsWith(prefix)),
  },

  /* ----------------------------------------------------------------- text */
  {
    name: 'figlet',
    category: 'text',
    aliases: ['banner'],
    summary: { en: 'render big block letters', ru: 'крупные буквы' },
    usage: 'figlet <text>',
    run(args) {
      const value = args.join(' ') || site.name
      const rows = figlet(value)
      return rows.length ? ok(rows.map((row) => text(row, 'accent'))) : err('figlet: nothing to render', 2)
    },
  },
  {
    name: 'cowsay',
    category: 'text',
    summary: { en: 'a cow says it better', ru: 'корова скажет лучше' },
    usage: 'cowsay <text>',
    run: (args) => ok(cowsay(args.join(' ') || site.bio).map((row) => text(row, 'accent')), { stream: 18 }),
  },
  filterCommand('grep', 'text'),
  filterCommand('head', 'text'),
  filterCommand('tail', 'text'),
  filterCommand('wc', 'text'),
  filterCommand('sort', 'text'),
  filterCommand('uniq', 'text'),
  filterCommand('nl', 'text'),
]

/* --------------------------------------------------------------- registry */

const index = new Map<string, Command>()
for (const command of commands) {
  index.set(command.name, command)
  for (const alias of command.aliases ?? []) index.set(alias, command)
}

export const find = (name: string): Command | undefined => index.get(name.toLowerCase())

export const allNames = (): string[] => [...index.keys()].sort()

export const visibleNames = (): string[] =>
  [...index.entries()]
    .filter(([, command]) => !command.hidden)
    .map(([name]) => name)
    .sort()

const CATEGORY_TITLES = {
  shell: { en: 'shell', ru: 'оболочка' },
  files: { en: 'files', ru: 'файлы' },
  system: { en: 'system', ru: 'система' },
  site: { en: 'site', ru: 'сайт' },
  text: { en: 'text', ru: 'текст' },
} as const

export function helpLines(state: ShellState, only?: string): Line[] {
  const categories = (Object.keys(CATEGORY_TITLES) as (keyof typeof CATEGORY_TITLES)[]).filter(
    (category) => !only || category === only,
  )
  if (!categories.length) return [text(`help: ${only}: unknown category`, 'err')]

  const width = Math.max(...commands.map((command) => command.name.length)) + 2
  return [
    line(span(t('helpTitle', state.locale), 'key')),
    ...categories.flatMap((category) => [
      blank(),
      line(span(`${CATEGORY_TITLES[category][state.locale]}`, 'accent')),
      ...commands
        .filter((command) => command.category === category && !command.hidden)
        .map((command) =>
          line(
            span('  '),
            span(command.name.padEnd(width), 'exec'),
            span(command.summary[state.locale], 'dim'),
          ),
        ),
    ]),
    blank(),
    text(t('helpFooter', state.locale), 'dim'),
  ]
}

export function manLines(command: Command, state: ShellState): Line[] {
  const aliases = command.aliases?.length
    ? [line(span('ALIASES', 'key')), text(`       ${command.aliases.join(', ')}`), blank()]
    : []
  return [
    line(
      span(`${command.name.toUpperCase()}(1)`, 'key'),
      span('                    General Commands Manual                    ', 'dim'),
      span(`${command.name.toUpperCase()}(1)`, 'key'),
    ),
    blank(),
    line(span('NAME', 'key')),
    line(span(`       ${command.name}`, 'exec'), span(` — ${command.summary[state.locale]}`)),
    blank(),
    line(span('SYNOPSIS', 'key')),
    text(`       ${command.usage}`),
    blank(),
    line(span('DESCRIPTION', 'key')),
    ...(command.man?.[state.locale] ?? [command.summary[state.locale]]).map((row) => text(`       ${row}`)),
    blank(),
    ...aliases,
    line(span('SEE ALSO', 'key')),
    text('       help(1)'),
  ]
}

/** Advertised in the boot banner. */
export const commandCount = commands.filter((command) => !command.hidden).length
